import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { formatApiError, resolveImageUrl } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, MapPin, Mic, ExternalLink, Eye, Trophy, Zap, ChevronLeft, ChevronRight, X } from 'lucide-react';

export default function InterviewPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/interviews/${slug}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(formatApiError(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]"><div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-center px-4">
        <div>
          <h1 className="text-white font-heading text-2xl mb-3">Interview not found</h1>
          <p className="text-gray-400 text-sm mb-4">{error || 'This spotlight may have been removed.'}</p>
          <Link to="/"><Button variant="outline" className="border-[#262626] text-white"><ArrowLeft className="w-4 h-4 mr-2" />Home</Button></Link>
        </div>
      </div>
    );
  }

  const hero = resolveImageUrl(data.hero_image);
  const gallery = (data.gallery_images || []).map(resolveImageUrl).filter(Boolean);
  const nextImg = () => setLightboxIdx((i) => (i + 1) % gallery.length);
  const prevImg = () => setLightboxIdx((i) => (i - 1 + gallery.length) % gallery.length);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Cinematic hero */}
      <div className="relative overflow-hidden">
        {hero && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${hero})` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-[#0A0A0A]" />
        <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-transparent via-[#FF3B30] to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-2 bg-gradient-to-r from-transparent via-[#FF3B30] to-transparent" />

        <div className="relative container mx-auto px-4 pt-8 pb-16 md:pt-10 md:pb-20">
          <Link to="/" className="inline-flex items-center text-gray-300 hover:text-white text-sm mb-6">
            <ArrowLeft className="w-3 h-3 mr-1" /> Back to Home
          </Link>
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-2 bg-[#FF3B30] text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-sm">
              <Zap className="w-3 h-3" />Featured Interview
            </div>
            <div className="flex items-center gap-1 text-gray-400 text-xs">
              <Mic className="w-3 h-3" />Driver Spotlight
            </div>
          </div>
          <h1 className="font-heading font-black text-5xl md:text-7xl text-white leading-none tracking-tight" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.9)' }}>
            {data.driver_name}
          </h1>
          {data.team_name && (
            <p className="text-[#FF3B30] font-black text-xl md:text-2xl uppercase tracking-widest mt-3">
              {data.team_name}
            </p>
          )}
          <p className="text-gray-200 text-base md:text-xl mt-5 max-w-3xl leading-relaxed" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.8)' }}>
            {data.subtitle}
          </p>
          <div className="flex items-center gap-4 flex-wrap mt-6 text-sm text-gray-300">
            {data.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#FF3B30]" />{data.location}</span>}
            {data.sponsor_name && (
              <span className="flex items-center gap-1.5">
                <Trophy className="w-4 h-4 text-[#FF3B30]" />Sponsor:&nbsp;
                {data.sponsor_url ? (
                  <a href={data.sponsor_url} target="_blank" rel="noreferrer" className="text-white hover:text-[#FF3B30] font-bold">
                    {data.sponsor_name}
                  </a>
                ) : (
                  <span className="text-white font-bold">{data.sponsor_name}</span>
                )}
              </span>
            )}
            {data.views > 0 && <span className="flex items-center gap-1.5 text-gray-500"><Eye className="w-4 h-4" />{data.views} views</span>}
          </div>
        </div>
      </div>

      {/* Q&A */}
      <div className="container mx-auto px-4 py-14 max-w-4xl">
        <div className="space-y-8">
          {(data.qa || []).map((item, i) => (
            <div key={i} className="relative" data-testid={`interview-qa-${i}`}>
              <div className="flex items-start gap-3 mb-3">
                <span className="font-heading font-black text-3xl text-[#FF3B30] leading-none">Q{i + 1}.</span>
                <h3 className="font-heading font-bold text-white text-lg md:text-xl leading-snug pt-1">
                  {item.q || item.question}
                </h3>
              </div>
              <div className="pl-10 border-l-2 border-[#262626] ml-1">
                <p className="text-gray-200 text-base md:text-lg leading-relaxed whitespace-pre-line">
                  {item.a || item.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Gallery */}
        {gallery.length > 0 && (
          <div className="mt-16">
            <p className="tag text-[#FF3B30] mb-3">Photo Gallery</p>
            <h3 className="font-heading font-black text-3xl text-white mb-6">
              {data.gallery_title || `${data.team_name || data.driver_name} in Action`}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIdx(i)}
                  className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-[#262626] hover:border-[#FF3B30] transition-colors"
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sponsor footer */}
        {data.sponsor_name && (
          <Card className="mt-14 bg-gradient-to-br from-[#141414] via-[#0F0F0F] to-[#141414] border-[#262626] p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Presenting Sponsor</p>
                <p className="text-white font-heading font-black text-2xl">{data.sponsor_name}</p>
                {data.sponsor_tagline && (
                  <p className="text-gray-400 text-sm mt-1">{data.sponsor_tagline}</p>
                )}
              </div>
              {data.sponsor_url && (
                <a href={data.sponsor_url} target="_blank" rel="noreferrer">
                  <Button className="bg-[#FF3B30] hover:bg-[#E6352B]">
                    Visit {data.sponsor_name} <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxIdx(null)}
        >
          <button className="absolute top-4 right-4 text-white hover:text-[#FF3B30]" onClick={() => setLightboxIdx(null)} aria-label="Close">
            <X className="w-7 h-7" />
          </button>
          {gallery.length > 1 && (
            <>
              <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-[#FF3B30] p-2" onClick={(e) => { e.stopPropagation(); prevImg(); }} aria-label="Previous">
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-[#FF3B30] p-2" onClick={(e) => { e.stopPropagation(); nextImg(); }} aria-label="Next">
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
          <img
            src={gallery[lightboxIdx]}
            alt=""
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
